/**
 * Entity → filtering metadata bindings.
 * Expand during P5/P6 consumer migration.
 *
 * filterArrayName must match `state.data` keys (ADR-F006).
 */
export const ENTITY_FILTER_REGISTRY = {
  company: {
    entityKey: "company",
    filterArrayName: "companyFilters",
    defaultSort: "-createdAt",
    /** Extra RTK list query args merged into buildListQueryArgs */
    listExtras: { context: "company" },
  },
  plan: {
    entityKey: "plan",
    filterArrayName: "planFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    /**
     * Preserve Plan list wire format during FE platform migration.
     * Defer protocol standardization until all consumers are migrated.
     */
    serializeOptions: { arrayJoin: ", " },
  },
  product: {
    entityKey: "product",
    filterArrayName: "productsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    /** Same Plan-era wire adapter — preserve until protocol standardization */
    serializeOptions: { arrayJoin: ", " },
  },
  market: {
    entityKey: "market",
    filterArrayName: "marketsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize (truthy strip, keep arrays) — no arrayJoin
  },
  billingUnit: {
    entityKey: "billingUnit",
    filterArrayName: "billingFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  systemFeature: {
    entityKey: "systemFeature",
    filterArrayName: "featureFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  usageCompilation: {
    entityKey: "usageCompilation",
    filterArrayName: "usageCompilationsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  usageSource: {
    entityKey: "usageSource",
    filterArrayName: "usageSourcesFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  user: {
    entityKey: "user",
    filterArrayName: "usersFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
    // Pagination converges to local useFilterQuery (was Redux); UX unchanged
  },
  contact: {
    entityKey: "contact",
    filterArrayName: "contactsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    /**
     * Preserve Contact list wire format (arrays joined with ", ").
     * Same Plan/Product adapter — no platform core change.
     */
    serializeOptions: { arrayJoin: ", " },
  },
  role: {
    entityKey: "role",
    filterArrayName: "rolesFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
    // Fixes legacy Filter prop typo: roleFilters → rolesFilters (dataSlice key)
  },
  export: {
    entityKey: "export",
    filterArrayName: "exportFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
    // Metadata via dataApiSlice mutation + dataSlice (was RTK Query-only, missing data key)
  },
  payment: {
    entityKey: "payment",
    filterArrayName: "paymentsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    /**
     * Preserve Payments list wire format (arrays joined with ", ").
     * Same Plan/Product/Contact adapter — no platform core change.
     */
    serializeOptions: { arrayJoin: ", " },
  },
  permission: {
    entityKey: "permission",
    filterArrayName: "permissionFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  import: {
    entityKey: "import",
    filterArrayName: "importsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
    // Pagination converges to local useFilterQuery (was Redux)
  },
  proposal: {
    entityKey: "proposal",
    filterArrayName: "proposalFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  upsellDownsell: {
    entityKey: "upsellDownsell",
    filterArrayName: "upsellDownsellFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  subscription: {
    entityKey: "subscription",
    filterArrayName: "subscriptionsFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    /**
     * Preserve Subscriptions list wire format (arrays joined with ", ").
     * Same Plan/Product/Contact/Payment adapter — no platform core change.
     */
    serializeOptions: { arrayJoin: ", " },
  },
  invoice: {
    entityKey: "invoice",
    filterArrayName: "invoiceFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    /**
     * Preserve Invoice list wire format (arrays joined with ", ").
     * Same Plan/Product/Contact/Payment/Subscription adapter.
     */
    serializeOptions: { arrayJoin: ", " },
  },
  selfServeUser: {
    entityKey: "selfServeUser",
    filterArrayName: "selfServeUserFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
    // Selected filters live on selfServeCheckout slice (split from customer)
  },
  billingLogic: {
    entityKey: "billingLogic",
    filterArrayName: "billingLogicFilters",
    // Flow subdocuments have no persisted timestamps (see
    // getBillingSettings/flowStatus.js on the backend) - "id" (a
    // Date.now() value assigned at creation) is the real stand-in for
    // "newest first".
    defaultSort: "-id",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
  usageCollection: {
    entityKey: "usageCollection",
    filterArrayName: "usageCollectionFilters",
    defaultSort: "-createdAt",
    listExtras: {},
    // Company-style serialize — no arrayJoin
  },
};

/**
 * @param {string} entityKey
 * @returns {typeof ENTITY_FILTER_REGISTRY[string]}
 */
export function getEntityFilterConfig(entityKey) {
  const config = ENTITY_FILTER_REGISTRY[entityKey];
  if (!config) {
    throw new Error(
      `Unknown filtering entity "${entityKey}". Register it in ENTITY_FILTER_REGISTRY.`
    );
  }
  return config;
}
